/**
 * Rule 26 — the shared "why did this fail, and what can I do" block.
 *
 * Every failure surface renders through this component so the same underlying
 * error explains itself identically on /pipeline, /work-queue and /logs. The
 * backend supplies `cause` + `remedies` from one catalog; this is the other
 * half of that contract.
 *
 * `auto` remedies render as buttons ONLY when the host passes an `actions` map
 * — the page is the only thing that knows how to perform a retry or a delete
 * for its own row shape. An auto remedy whose action is absent from the map is
 * dropped, which keeps the guarantee that matters: never a button that does
 * nothing.
 *
 * A surface that passes no map at all drops every auto remedy. That is a
 * legitimate choice for a summary card (deep-link instead of act) or for a
 * surface with no matching capability (a log row cannot be "retried") — but it
 * must be a CHOICE. It was previously the accidental default everywhere, so 42
 * auto remedies in the catalog rendered on exactly one page.
 */

import styles from "./FailureExplanation.module.css";

export interface Remedy {
	kind: "auto" | "manual" | "code";
	label: string;
	detail?: string;
	action?: string;
}

interface Props {
	/** Plain-language cause from the backend. Absent = nothing to render. */
	cause?: string;
	remedies?: Remedy[];
	/**
	 * Raw error text — traceback, reason string, provider message.
	 * Demoted into a collapsed block, never dropped: the cause is the system's
	 * best guess, and this is the evidence that lets a user tell when the guess
	 * is wrong.
	 */
	technical?: string;
	/** Label for the collapsed block. */
	technicalLabel?: string;
	/**
	 * Handlers this surface can actually perform, keyed by remedy `action`.
	 * Auto remedies not present here are dropped rather than rendered dead.
	 */
	actions?: Record<string, () => void | Promise<void>>;
	/** Disables every action button (e.g. while a request is in flight). */
	actionsBusy?: boolean;
}

export function FailureExplanation({
	cause,
	remedies,
	technical,
	technicalLabel = "Technical detail",
	actions,
	actionsBusy = false,
}: Props) {
	const actionable = (remedies || []).filter((r) => r.kind !== "auto");
	const raw = (technical || "").trim();

	// Auto remedies this surface can actually carry out, de-duplicated so a
	// catalog listing the same action twice yields one button.
	const seen = new Set<string>();
	const performable = (remedies || []).filter((r) => {
		if (r.kind !== "auto" || !r.action) return false;
		if (!actions || !(r.action in actions)) return false;
		if (seen.has(r.action)) return false;
		seen.add(r.action);
		return true;
	});

	if (!cause && actionable.length === 0 && performable.length === 0 && !raw) {
		return null;
	}

	return (
		<div className={styles.wrap}>
			{cause && <div className={styles.cause}>{cause}</div>}

			{actionable.length > 0 && (
				<ul className={styles.remedies}>
					{actionable.map((remedy, i) => (
						<li className={styles.remedy} key={`${remedy.kind}-${i}`}>
							<span
								className={`${styles.kind} ${
									remedy.kind === "code" ? styles.kind_code : ""
								}`}
								title={
									remedy.kind === "code"
										? "Needs a code change — filed as a proposal so it is not forgotten"
										: "Something for you to do"
								}
							>
								{remedy.kind === "code" ? "code" : "you"}
							</span>
							<span>
								{remedy.label}
								{remedy.detail && (
									<span className={styles.detail}>{remedy.detail}</span>
								)}
							</span>
						</li>
					))}
				</ul>
			)}

			{performable.length > 0 && (
				<div className={styles.actions}>
					{performable.map((remedy) => (
						<button
							type="button"
							key={remedy.action}
							className={styles.actionBtn}
							disabled={actionsBusy}
							title={remedy.detail || remedy.label}
							onClick={() => void actions?.[remedy.action!]?.()}
						>
							{remedy.label}
						</button>
					))}
				</div>
			)}

			{raw && (
				<details className={styles.technical}>
					<summary>{technicalLabel}</summary>
					<div className={styles.technicalBody}>{raw}</div>
				</details>
			)}
		</div>
	);
}

export default FailureExplanation;
